/**
 * Dialog to create, edit and delete a weekly group combination (#1579 part 3).
 * A dialog form (docs/coding-standards/frontend.md): it drives one action, so it uses
 * shadcn Form instead of BaseEditableForm.
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react';
import { AlertTriangle, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/api';
import {
  useCreateGroupCombination,
  useDeleteGroupCombination,
  useUpdateGroupCombination,
} from '@/hooks/useGroupCombinations';
import type { CombinationSuggestion, GroupCombination } from '@/types/groupCombinations';
import {
  MAX_JOINING_GROUPS,
  QUARTER_HOUR_OPTIONS,
  combinationToFormDefaults,
  getGroupCombinationSchema,
  suggestionToFormDefaults,
  toGroupCombinationRequest,
  type GroupCombinationFormData,
} from '@/schemas/groupCombinationSchema';
import { WeekdayMultiSelect } from './WeekdayMultiSelect';

export interface CombinableGroup {
  id?: string;
  name?: string;
  childcare_id?: string;
}

export interface CombineGroupsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: CombinableGroup[];
  /** The combination to edit. Absent or null creates a new one. */
  combination?: GroupCombination | null;
  /** Fills in a new combination from a suggestion (#1579 part 4). Ignored in edit mode. */
  suggestion?: CombinationSuggestion | null;
  /** valid_from of a new combination. */
  defaultValidFrom: Date;
}

const formDefaults = (
  combination: GroupCombination | null | undefined,
  suggestion: CombinationSuggestion | null | undefined,
  defaultValidFrom: Date
): GroupCombinationFormData =>
  !combination && suggestion
    ? suggestionToFormDefaults(suggestion, defaultValidFrom)
    : combinationToFormDefaults(combination, defaultValidFrom);

const OPTIMISTIC_LOCK_MESSAGE = 'group combination was modified by another user';

/** The server message of a 400 or 409, else null. */
const serverMessage = (error: unknown): string | null => {
  if (error instanceof ApiError && (error.status === 400 || error.status === 409)) {
    const message = (error.body as { message?: unknown } | undefined)?.message;
    return typeof message === 'string' && message !== '' ? message : null;
  }
  return null;
};

export function CombineGroupsDialog({
  open,
  onOpenChange,
  groups,
  combination,
  suggestion,
  defaultValidFrom,
}: CombineGroupsDialogProps) {
  const { i18n } = useLingui();
  const createCombination = useCreateGroupCombination();
  const updateCombination = useUpdateGroupCombination();
  const deleteCombination = useDeleteGroupCombination();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [optimisticLockError, setOptimisticLockError] = useState(false);
  // Delete asks for confirmation inline: a second Radix dialog would nest focus traps.
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isEdit = !!combination;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild the messages on a locale switch
  const schema = useMemo(() => getGroupCombinationSchema(i18n), [i18n.locale]);
  const form = useForm<GroupCombinationFormData>({
    resolver: zodResolver(schema),
    defaultValues: formDefaults(combination, suggestion, defaultValidFrom),
  });

  useEffect(() => {
    if (open) {
      form.reset(formDefaults(combination, suggestion, defaultValidFrom));
      setSubmitError(null);
      setOptimisticLockError(false);
      setConfirmDelete(false);
    }
    // defaultValidFrom is a fresh Date on each render of the page; the combination
    // id, the suggestion and the open flag decide when the form resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, combination?.id, combination?.updated_at, suggestion]);

  const hostGroupId = useWatch({ control: form.control, name: 'hostGroupId' });
  const memberGroupIds = useWatch({ control: form.control, name: 'memberGroupIds' });

  const namedGroups = useMemo(
    () =>
      groups
        .filter((g): g is Required<Pick<CombinableGroup, 'id' | 'name'>> & CombinableGroup => !!g.id && !!g.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [groups]
  );
  const hostChildcareId = namedGroups.find((g) => g.id === hostGroupId)?.childcare_id;
  const joinableGroups = useMemo(
    () =>
      hostGroupId
        ? namedGroups.filter((g) => g.id !== hostGroupId && !!hostChildcareId && g.childcare_id === hostChildcareId)
        : [],
    [namedGroups, hostGroupId, hostChildcareId]
  );

  // A host change drops joining groups that are no longer allowed.
  useEffect(() => {
    const allowed = new Set(joinableGroups.map((g) => g.id));
    const kept = memberGroupIds.filter((id) => allowed.has(id));
    if (kept.length !== memberGroupIds.length) {
      form.setValue('memberGroupIds', kept, { shouldValidate: form.formState.isSubmitted });
    }
  }, [joinableGroups, memberGroupIds, form]);

  const isSubmitting = createCombination.isPending || updateCombination.isPending;

  const handleError = (error: unknown, fallback?: string) => {
    const message = serverMessage(error);
    if (message === OPTIMISTIC_LOCK_MESSAGE) {
      setOptimisticLockError(true);
      return;
    }
    setSubmitError(
      message ??
        fallback ??
        i18n._({ id: 'shifts.combination.error.generic', message: 'The combination could not be saved.' })
    );
  };

  const handleSubmit = async (data: GroupCombinationFormData) => {
    setSubmitError(null);
    setOptimisticLockError(false);
    const request = toGroupCombinationRequest(data);
    try {
      if (combination) {
        await updateCombination.mutateAsync({
          id: combination.id,
          request: { ...request, current_updated_at: combination.updated_at },
        });
      } else {
        await createCombination.mutateAsync(request);
      }
      onOpenChange(false);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDelete = async () => {
    if (!combination) return;
    try {
      await deleteCombination.mutateAsync(combination.id);
      setConfirmDelete(false);
      onOpenChange(false);
    } catch (error) {
      setConfirmDelete(false);
      handleError(
        error,
        i18n._({ id: 'shifts.combination.error.deleteFailed', message: 'The combination could not be deleted.' })
      );
    }
  };

  // Refresh reloads the combinations; the page passes the new version back in.
  const handleRefresh = () => {
    setOptimisticLockError(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="combine-groups-dialog">
          <DialogHeader>
            <DialogTitle>
              {isEdit
                ? i18n._({ id: 'shifts.combination.dialog.editTitle', message: 'Edit group combination' })
                : i18n._({ id: 'shifts.combination.dialog.createTitle', message: 'Combine groups' })}
            </DialogTitle>
            <DialogDescription>
              {i18n._({
                id: 'shifts.combination.dialog.description',
                message:
                  'The groups share the room and capacity of the host group during the time window. Shifts stay in their own groups.',
              })}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="hostGroupId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._({ id: 'shifts.combination.field.host', message: 'Host group' })}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="combination-host-select">
                          <SelectValue
                            placeholder={i18n._({ id: 'shifts.combination.field.hostPlaceholder', message: 'Select the group whose room is used' })}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {namedGroups.map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="memberGroupIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._({ id: 'shifts.combination.field.members', message: 'Joining groups' })}</FormLabel>
                    {!hostGroupId ? (
                      <p className="text-sm text-muted-foreground">
                        {i18n._({ id: 'shifts.combination.field.membersHint', message: 'Select the host group first.' })}
                      </p>
                    ) : joinableGroups.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {i18n._({ id: 'shifts.combination.field.membersNone', message: 'No other group belongs to the same childcare.' })}
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2" data-testid="combination-member-list">
                        {joinableGroups.map((g) => {
                          const checked = field.value.includes(g.id);
                          return (
                            <label key={g.id} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={checked}
                                disabled={!checked && field.value.length >= MAX_JOINING_GROUPS}
                                onCheckedChange={(next) =>
                                  field.onChange(
                                    next === true
                                      ? [...field.value, g.id]
                                      : field.value.filter((id) => id !== g.id)
                                  )
                                }
                              />
                              {g.name}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="weekdays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._({ id: 'shifts.combination.field.weekdays', message: 'Weekdays' })}</FormLabel>
                    <FormControl>
                      <WeekdayMultiSelect value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                {(['startTime', 'endTime'] as const).map((name) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {name === 'startTime'
                            ? i18n._({ id: 'shifts.combination.field.from', message: 'From' })
                            : i18n._({ id: 'shifts.combination.field.to', message: 'To' })}
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger data-testid={`combination-${name}`}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-64">
                            {QUARTER_HOUR_OPTIONS.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="validFrom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{i18n._({ id: 'shifts.combination.field.validFrom', message: 'Valid from' })}</FormLabel>
                      <FormControl>
                        <DatePicker value={field.value} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="validTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{i18n._({ id: 'shifts.combination.field.validTo', message: 'Valid to (optional)' })}</FormLabel>
                      <FormControl>
                        <DatePicker value={field.value} onChange={field.onChange} showClearButton />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._({ id: 'shifts.combination.field.note', message: 'Note (optional)' })}</FormLabel>
                    <FormControl>
                      <Textarea rows={2} maxLength={500} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {optimisticLockError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>
                    {i18n._({
                      id: 'shifts.combination.error.optimisticLock',
                      message: 'This combination was changed by someone else.',
                    })}
                  </AlertTitle>
                  <AlertDescription className="mt-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleRefresh}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {i18n._({ id: 'shifts.combination.error.refresh', message: 'Refresh' })}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              {submitError && (
                <p className="text-sm font-medium text-destructive" role="alert" data-testid="combination-submit-error">
                  {submitError}
                </p>
              )}

              {confirmDelete ? (
                <div className="space-y-3 rounded-md border border-destructive/40 p-3" role="alert" data-testid="combination-delete-confirm">
                  <p className="text-sm font-medium">
                    {i18n._({ id: 'shifts.combination.delete.title', message: 'Delete this combination?' })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {i18n._({
                      id: 'shifts.combination.delete.description',
                      message: 'The groups are planned separately again on every weekday of this combination.',
                    })}
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>
                      {i18n._({ id: 'common.cancel', message: 'Cancel' })}
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => void handleDelete()}
                      disabled={deleteCombination.isPending}
                    >
                      {deleteCombination.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {i18n._({ id: 'shifts.combination.delete.confirm', message: 'Delete combination' })}
                    </Button>
                  </div>
                </div>
              ) : (
                <DialogFooter className="gap-2 sm:justify-between">
                  {isEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="text-destructive"
                      onClick={() => setConfirmDelete(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {i18n._({ id: 'common.delete', message: 'Delete' })}
                    </Button>
                  ) : (
                    <span />
                  )}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      {i18n._({ id: 'common.cancel', message: 'Cancel' })}
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {i18n._({ id: 'common.save', message: 'Save' })}
                    </Button>
                  </div>
                </DialogFooter>
              )}
            </form>
          </Form>
        </DialogContent>
    </Dialog>
  );
}
